import React from "react";
import styled, { keyframes } from "styled-components";
import { BedDouble, User } from "lucide-react";
import { useLanguage } from "../contexts/language";
import { useRoom } from "../contexts/room";
import { LanguageSwitcher } from "./language-switcher";
import { unmodule } from "../util";

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  background-color: #fff;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border-top: 3px solid #c9a961;
  animation: ${fadeInScale} 0.25s ease-out;
  margin: 0 16px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Logo = styled.img`
  height: 48px;
  width: auto;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0;
  border-radius: 10px;
  background: #f3f3f3;
  padding: 3px;
  margin-bottom: 20px;
`;

const Tab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$active ? "#fff" : "transparent")};
  color: ${(props) => (props.$active ? "#333" : "#6b7280")};
  box-shadow: ${(props) =>
    props.$active ? "0 1px 4px rgba(0,0,0,0.08)" : "none"};

  svg {
    color: ${(props) => (props.$active ? "#c9a961" : "#aaa")};
  }
`;

const FormArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #333;
`;

const Hint = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
`;

const Input = styled.input`
  margin-top: 6px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  background-color: #f9f9f9;
  padding: 11px 14px;
  color: #333;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    border-color: #c9a961;
    box-shadow: 0 0 0 1px #c9a961;
    background: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  border-radius: 8px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  font-size: 13px;
  color: #b91c1c;
`;

const SubmitButton = styled.button`
  width: 100%;
  border-radius: 10px;
  background-color: #c9a961;
  padding: 13px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background-color: #b89451;
    box-shadow: 0 4px 12px rgba(201, 169, 97, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const RoomInputModal = ({ onRoomVerified, buildingCode }) => {
  const { t } = useLanguage();
  const { setGuestProfile, isLoading, error, guestName, roomNumber } =
    useRoom();
  const [mode, setMode] = React.useState(roomNumber ? "guest" : "guest");
  const [nameValue, setNameValue] = React.useState(guestName || "");
  const [roomValue, setRoomValue] = React.useState(roomNumber || "");
  const [localError, setLocalError] = React.useState(null);

  const switchMode = (newMode) => {
    setMode(newMode);
    setLocalError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setLocalError(null);

    if (mode === "guest") {
      if (!roomValue.trim()) {
        setLocalError(t.room.errorRoomRequired);
        return;
      }
    } else {
      if (!nameValue.trim()) {
        setLocalError(t.room.errorNameRequired);
        return;
      }
    }

    const success = await setGuestProfile({
      name: nameValue.trim(),
      room: mode === "guest" ? roomValue.trim() : "",
      buildingCode,
    });
    if (success) {
      onRoomVerified();
    } else {
      setLocalError(t.room.error);
    }
  };

  return (
    <ModalOverlay>
      <ModalContent>
        <TopRow>
          <Logo src={unmodule(require("../assets/logo-dark.png"))} alt="HEBE" />
          <LanguageSwitcher />
        </TopRow>

        <Title>{t.room.title}</Title>

        <TabRow>
          <Tab $active={mode === "guest"} onClick={() => switchMode("guest")}>
            <BedDouble size={16} />
            {t.room.tabGuest}
          </Tab>
          <Tab $active={mode === "walkin"} onClick={() => switchMode("walkin")}>
            <User size={16} />
            {t.room.tabWalkin}
          </Tab>
        </TabRow>

        <form onSubmit={handleSubmit}>
          <FormArea>
            {mode === "guest" ? (
              <>
                <FormGroup>
                  <Label htmlFor="room-input">{t.room.label}</Label>
                  <Input
                    id="room-input"
                    type="text"
                    inputMode="numeric"
                    value={roomValue}
                    onChange={(e) => {
                      setRoomValue(e.target.value);
                      setLocalError(null);
                    }}
                    placeholder={t.room.placeholder}
                    autoFocus
                  />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="guest-name-input">{t.room.nameLabel}</Label>
                  <Hint>{t.room.nameOptionalHint}</Hint>
                  <Input
                    id="guest-name-input"
                    type="text"
                    value={nameValue}
                    onChange={(e) => {
                      setNameValue(e.target.value);
                      setLocalError(null);
                    }}
                    placeholder={t.room.namePlaceholder}
                  />
                </FormGroup>
              </>
            ) : (
              <FormGroup>
                <Label htmlFor="walkin-name-input">{t.room.nameLabel}</Label>
                <Hint>{t.room.walkinHint}</Hint>
                <Input
                  id="walkin-name-input"
                  type="text"
                  value={nameValue}
                  onChange={(e) => {
                    setNameValue(e.target.value);
                    setLocalError(null);
                  }}
                  placeholder={t.room.namePlaceholder}
                  autoFocus
                />
              </FormGroup>
            )}

            {(localError || error) && (
              <ErrorMessage>{localError || error}</ErrorMessage>
            )}

            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? t.room.loading : t.room.submit}
            </SubmitButton>
          </FormArea>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};
